import {
  type ChangeEvent,
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import TextInput from '__components/Inputs/TextInput';
import {
  type OnlineSourceListItem,
  type OnlineSource,
} from '@kizen/api/knowledge';
import BasicModal from '__components/Modals/presets/BasicModal';
import ConfirmationModal from '__components/Modals/ConfirmationModal';
import LongText from '__components/Inputs/LongText';
import Cols from '__components/Layout/Cols';
import DecimalNumberTextInput from '__components/Inputs/TextInput/presets/DecimalNumber';
import Switch from '__components/Kizen/Switch';
import { useToast } from '__components/ToastProvider';
import KnowledgeService from 'services/KnowledgeService';
import { getErrorMessage } from '__hooks/useErrors';
import { getOriginalError } from '__services/AxiosService';
import { useFlashTransition } from 'hooks/useFlashState';
import { getCanStartCrawl } from './helpers';

export type OnlineSourceData = Pick<
  OnlineSourceListItem,
  | 'id'
  | 'base_url'
  | 'description'
  | 'include_patterns'
  | 'exclude_patterns'
  | 'mode'
  | 'max_pages'
  | 'max_depth'
  | 'respect_robots_txt'
  | 'automatically_activate'
  | 'is_active'
  | 'latest_execution'
  | 'active_execution'
  | 'combined_status'
>;

type OnlineSourceFormData = Omit<
  OnlineSourceData,
  | 'id'
  | 'include_patterns'
  | 'exclude_patterns'
  | 'latest_execution'
  | 'active_execution'
  | 'is_active'
  | 'max_pages'
  | 'max_depth'
  | 'combined_status'
> & {
  include_patterns: string;
  exclude_patterns: string;
  max_pages: string;
  max_depth: string;
};

type OnlineSourceModalProps = {
  onlineSource: OnlineSourceData;
  isStartModal: boolean;
  onConfirm: () => void;
  onHide: () => void;
  catalogId?: string;
};

const errorsOrder: Record<keyof OnlineSourceFormData, number> = {
  base_url: 1,
  description: 2,
  include_patterns: 3,
  exclude_patterns: 4,
  max_pages: 5,
  max_depth: 6,
  mode: 7,
  respect_robots_txt: 8,
  automatically_activate: 9,
};

const getPayload = (data: Partial<OnlineSourceFormData>) => ({
  ...data,
  include_patterns: data.include_patterns?.split('\n').filter(Boolean),
  exclude_patterns: data.exclude_patterns?.split('\n').filter(Boolean),
  max_pages: data.max_pages ? Number(data.max_pages) : 0,
  max_depth: data.max_depth ? Number(data.max_depth) : 0,
});

const getFormData = (data: OnlineSourceData): OnlineSourceFormData => {
  const {
    id,
    latest_execution,
    active_execution,
    is_active,
    combined_status,
    ...rest
  } = data;
  return {
    ...rest,
    include_patterns: (data.include_patterns || []).join('\n'),
    exclude_patterns: (data.exclude_patterns || []).join('\n'),
    max_pages: data.max_pages ? data.max_pages.toString() : '',
    max_depth: data.max_depth ? data.max_depth.toString() : '',
  };
};

type FormDataErrors = Partial<Record<keyof OnlineSource, string[] | null>>;

export const OnlineSourceModal: FC<OnlineSourceModalProps> = ({
  onlineSource,
  isStartModal,
  onConfirm,
  onHide,
  catalogId,
}) => {
  const { t } = useTranslation();
  const [showToast, toastVariant] = useToast();
  const [, showErrorMessage, flashError, cancelFlash] = useFlashTransition();

  const [values, setValues] = useState<OnlineSourceFormData>(
    getFormData(onlineSource)
  );

  const [errors, setErrors] = useState<FormDataErrors>({});

  const onChangeValue = useCallback(
    (field: keyof Partial<OnlineSource>, value: string) => {
      setValues((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: null }));
      cancelFlash();
    },
    [cancelFlash]
  );

  const onToggleValue = useCallback(
    (field: keyof Partial<OnlineSource>, value: boolean) => {
      setValues((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: null }));
      cancelFlash();
    },
    [cancelFlash]
  );

  const onBaseUrlChange = useCallback(
    (value: string) => {
      onChangeValue('base_url', value);
    },
    [onChangeValue]
  );

  const onDescriptionChange = useCallback(
    (value: string) => {
      onChangeValue('description', value);
    },
    [onChangeValue]
  );

  const onIncludePatternsChange = useCallback(
    (value: string) => {
      onChangeValue('include_patterns', value);
    },
    [onChangeValue]
  );

  const onExcludePatternsChange = useCallback(
    (value: string) => {
      onChangeValue('exclude_patterns', value);
    },
    [onChangeValue]
  );

  const onMaxPagesChange = useCallback(
    (value: string) => {
      onChangeValue('max_pages', value);
    },
    [onChangeValue]
  );

  const onMaxDepthChange = useCallback(
    (value: string) => {
      onChangeValue('max_depth', value);
    },
    [onChangeValue]
  );

  const onRespectRobotsTxtChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onToggleValue('respect_robots_txt', e.target.checked);
    },
    [onToggleValue]
  );

  const onAutomaticallyActivateChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onToggleValue('automatically_activate', e.target.checked);
    },
    [onToggleValue]
  );

  const onSubmit = useCallback(
    async (
      onlineSourceId: string | null,
      payload: Partial<OnlineSource>,
      action: 'start' | 'save' | 'save_and_start'
    ) => {
      try {
        if (onlineSourceId) {
          if (action === 'save' || action === 'save_and_start') {
            await KnowledgeService.updateOnlineSource(onlineSourceId, payload);
            showToast({
              message: t('Online Source updated successfully'),
              variant: toastVariant.SUCCESS,
            });
          }
          if (action === 'start' || action === 'save_and_start') {
            await KnowledgeService.startOnlineSourceExecution(onlineSourceId);
            showToast({
              message: t('Online Source started successfully'),
              variant: toastVariant.SUCCESS,
            });
          }
        } else {
          const source = await KnowledgeService.createOnlineSource({
            ...(payload as OnlineSource),
            ...(catalogId ? { catalog_id: catalogId } : {}),
          });
          showToast({
            message: t('Online Source added successfully'),
            variant: toastVariant.SUCCESS,
          });
          if (action === 'save_and_start') {
            await KnowledgeService.startOnlineSourceExecution(source.id);
            showToast({
              message: t('Online Source started successfully'),
              variant: toastVariant.SUCCESS,
            });
          }
        }
        onConfirm();
      } catch (error) {
        const nextErrors: FormDataErrors = getOriginalError(error);
        if (
          nextErrors &&
          (nextErrors.base_url ||
            nextErrors.description ||
            nextErrors.mode ||
            nextErrors.include_patterns ||
            nextErrors.exclude_patterns ||
            nextErrors.max_pages ||
            nextErrors.max_depth ||
            nextErrors.respect_robots_txt ||
            nextErrors.automatically_activate)
        ) {
          setErrors(nextErrors);
        } else {
          const message =
            getErrorMessage(nextErrors) || t('Failed to add Online Source');
          showToast({
            message,
            variant: toastVariant.FAILURE,
          });
        }
      }
    },
    [showToast, t, toastVariant, onConfirm, catalogId]
  );

  const handleSave = useCallback(async () => {
    await onSubmit(onlineSource.id, getPayload(values), 'save');
  }, [onSubmit, values, onlineSource.id]);

  const handleStart = useCallback(async () => {
    await onSubmit(onlineSource.id, getPayload({ mode: values.mode }), 'start');
  }, [onSubmit, values, onlineSource.id]);

  const handleSaveAndStart = useCallback(async () => {
    await onSubmit(onlineSource.id, getPayload(values), 'save_and_start');
  }, [onSubmit, values, onlineSource.id]);

  const firstErrorKey = useMemo(() => {
    return Object.keys(errors)
      .filter((key) => errors[key as keyof OnlineSourceFormData])
      .sort((a, b) => {
        return (
          errorsOrder[a as keyof OnlineSourceFormData] -
          errorsOrder[b as keyof OnlineSourceFormData]
        );
      })[0] as keyof OnlineSource | undefined;
  }, [errors]);

  const firstErrorMessage = firstErrorKey && errors[firstErrorKey];

  const validate = useMemo(() => {
    if (firstErrorMessage) {
      return {
        showMessage: showErrorMessage,
        message: firstErrorMessage || [],
      };
    }
    return undefined;
  }, [firstErrorMessage, showErrorMessage]);

  useEffect(() => {
    if (firstErrorMessage) {
      flashError(firstErrorMessage);
    }
    () => cancelFlash();
  }, [firstErrorMessage, flashError, cancelFlash]);

  const canStart = getCanStartCrawl(onlineSource);

  const onHideWithConfirmation = useCallback(() => {
    const payload = getPayload(values);
    const originalPayload = getPayload(getFormData(onlineSource));
    const isDirty = JSON.stringify(payload) !== JSON.stringify(originalPayload);
    if (isDirty) {
      setIsConfirmModalOpen(true);
    } else {
      onHide();
    }
  }, [onHide, values, onlineSource]);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const onHideConfirmed = useCallback(() => {
    setIsConfirmModalOpen(false);
    onHide();
  }, [onHide]);

  const onHideCancelled = useCallback(() => {
    setIsConfirmModalOpen(false);
  }, []);

  return isStartModal ? (
    <ConfirmationModal
      heading={t('Start Online Crawl')}
      buttonText={t('Start')}
      defaultLeftBtnText={t('Close')}
      show
      onConfirm={handleStart}
      onHide={onHide}
    >
      {t(
        'This will replace your existing pages saved on this source after completion of a run.'
      )}
    </ConfirmationModal>
  ) : (
    <BasicModal
      heading={
        onlineSource.id ? t('Edit Online Source') : t('New Online Source')
      }
      buttonText={canStart ? t('Save and Start') : t('Save')}
      disabled={false}
      show
      onConfirm={canStart ? handleSaveAndStart : handleSave}
      defaultLeftBtnText={t('Close')}
      additionalButtonText={canStart ? t('Save') : null}
      onAdditionalConfirm={handleSave}
      additionalButtonColor="green"
      onHide={onHideWithConfirmation}
    >
      <TextInput
        label={t('Starting URL')}
        placeholder="https://www.example.com"
        value={values.base_url || ''}
        onChange={onBaseUrlChange}
        labelInfo={t(
          'Fully qualified URL to start site traversal. Will only follow child pages on the same domain. Will also include /sitemap.xml if found.'
        )}
        labelInfoPlacement="top"
        margin
        error={!!errors.base_url}
        validate={firstErrorKey === 'base_url' ? validate : undefined}
      />
      <TextInput
        label={t('Description')}
        placeholder={t('Defaults to Hostname')}
        value={values.description || ''}
        onChange={onDescriptionChange}
        margin
        error={!!errors.description}
        validate={firstErrorKey === 'description' ? validate : undefined}
      />
      <LongText
        label={t('Include Patterns')}
        placeholder="e.g. /products/"
        labelInfo={t(
          'Page locations to include for knowledge and traversal. Non-matching pages will be omitted and not traversed. Newline separated.'
        )}
        labelInfoPlacement="top"
        value={values.include_patterns || ''}
        onChange={onIncludePatternsChange}
        initialHeight={70}
        margin
        error={!!errors.include_patterns}
        validate={firstErrorKey === 'include_patterns' ? validate : undefined}
      />
      <LongText
        label={t('Exclude Patterns')}
        placeholder="e.g. /products/"
        labelInfo={t(
          'Page locations excluded from knowledge sources and traversal. Newline separated.'
        )}
        labelInfoPlacement="top"
        value={values.exclude_patterns || ''}
        onChange={onExcludePatternsChange}
        initialHeight={70}
        margin
        error={!!errors.exclude_patterns}
        validate={firstErrorKey === 'exclude_patterns' ? validate : undefined}
      />
      <Cols gutter="20px" columns={2}>
        <DecimalNumberTextInput
          value={values.max_pages?.toString() || ''}
          label={t('Max Pages')}
          placeholder={t('0')}
          onChange={onMaxPagesChange}
          allowNegatives={false}
          decimalScale={0}
          decimalOptions={{
            minValue: 1,
            maxValue: 1000,
          }}
          thousandSeparator={false}
          margin
          error={!!errors.max_pages}
          validate={firstErrorKey === 'max_pages' ? validate : undefined}
        />
        <DecimalNumberTextInput
          value={values.max_depth?.toString() || ''}
          label={t('Max Depth')}
          placeholder={t('0')}
          onChange={onMaxDepthChange}
          allowNegatives={false}
          decimalScale={0}
          decimalOptions={{
            minValue: 1,
            maxValue: 10,
          }}
          thousandSeparator={false}
          margin
          error={!!errors.max_depth}
          validate={firstErrorKey === 'max_depth' ? validate : undefined}
        />
      </Cols>
      <Cols gutter="20px" columns={2}>
        <Switch
          label={t('Respect robots.txt')}
          tooltip={t(
            'When enabled, knowledge source pages will be automatically activated once successfully traversed and extracted without user review or approval.'
          )}
          textPlacement="regular-top"
          checked={values.respect_robots_txt ?? false}
          onChange={onRespectRobotsTxtChange}
          removeMargin
          disabled
        />
        <Switch
          label={t('Automatically Activate')}
          textPlacement="regular-top"
          checked={values.automatically_activate ?? false}
          onChange={onAutomaticallyActivateChange}
          removeMargin
          disabled
        />
      </Cols>
      <ConfirmationModal
        heading={t('You Have Unsaved Changes')}
        buttonText={t('Discard Changes')}
        defaultLeftBtnText={t('Cancel')}
        actionBtnColor="red"
        show={isConfirmModalOpen}
        onConfirm={onHideConfirmed}
        onHide={onHideCancelled}
      >
        {t('Unsaved changes will be lost, would you like to continue?')}
      </ConfirmationModal>
    </BasicModal>
  );
};
